import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { MdsDividerComponent } from '@medpacesoftwaredevelopment/designsystem';
import { PaymentRequestStatus, ReimbursementRequest } from '@models/request';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { PdfGenerationService } from '@services/pdf-generation/pdf-generation.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { RequestMerchantStateService } from '@services/state-management/request-merchant-state.service';
import { RequestStateService } from '@services/state-management/request-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { combineLatest, map, switchMap, take } from 'rxjs';
import { PaymentRequestDeductionViewComponent } from '../../payment-request-deduction/payment-request-deduction-view/payment-request-deduction-view.component';
import { ExpenseViewComponent } from '../expense/expense-view/expense-view.component';

@Component({
    selector: 'medpace-reimbursement-request-view',
    imports: [
        ExpenseViewComponent,
        dateFormatPipe,
        UpperCasePipe,
        AsyncPipe,
        DecimalPipe,
        PaymentRequestDeductionViewComponent,
        MdsDividerComponent,
    ],
    templateUrl: './reimbursement-request-view.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReimbursementRequestViewComponent {
    request = input.required<ReimbursementRequest>();
    request$ = toObservable(this.request);

    requestMerchantStateService = inject(RequestMerchantStateService);
    adminRequestService = inject(AdminRequestServices);
    requestStateService = inject(RequestStateService);
    patientStateService = inject(PatientStateService);
    studyStateService = inject(StudyStateService);
    pdfGenerationService = inject(PdfGenerationService);
    requestService = inject(ReimbursementRequestService);
    userService = inject(UserServiceV2);
    merchantTypes$ = this.requestMerchantStateService.getPaymentRequestMerchants();
    merchant$ = combineLatest({
        request: this.request$,
        merchantTypes: this.merchantTypes$,
    }).pipe(
        map(({ merchantTypes, request }) => {
            return merchantTypes?.find((merchant) => merchant.value === request.merchant);
        })
    );

    elevatedAccess$ = this.userService.permissions.request.access.elevated$;

    private readonly patient = toSignal(this.patientStateService.getPatient());
    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );

    totalUsdAmount = computed((): { label: string; text: string } | null => {
        const req = this.request();
        if (req.totalAmountAuthorizedUsd == null) return null;
        return { label: 'Total Authorized (USD)', text: `${req.totalAmountAuthorizedUsd.toFixed(2)}` };
    });

    totalPatientsCurrencyAmount = computed((): { label: string; text: string } | null => {
        const req = this.request();
        const patientCurrency = this.patientPreferredCurrency();
        if (!patientCurrency) return null;
        if (req.totalAmountAuthorizedPatientsCurrency == null) return null;
        if (patientCurrency === 'USD') return null;

        return {
            label: `Total Authorized (${patientCurrency})`,
            text: req.totalAmountAuthorizedPatientsCurrency.toFixed(2),
        };
    });

    isPreCurrencyConversionMigration = computed(() => {
        const request = this.request();

        return (
            request.totalAmountAuthorizedPatientsCurrency === null ||
            request.totalAmountAuthorizedPatientsCurrency === undefined ||
            request.totalAmountAuthorizedUsd === null ||
            request.totalAmountAuthorizedUsd === undefined
        );
    });

    downloadExpenseReport() {
        const request = this.request();
        const attachmentUrls = request.reimbursementTransactions
            .filter((expense) => expense.attachmentId)
            .map((expense) =>
                this.adminRequestService
                    .getReimbursementRequestAttachmentUrl(request.id, expense.attachmentId)
                    .toString()
            );

        combineLatest({
            patient: this.patientStateService.getPatient(),
            visitNames: this.requestStateService.getVisits(),
            availableExpenses: this.studyStateService.getAvailableExpenses(),
            elevatedAccess: this.elevatedAccess$,
            isInProcessOrHigher: this.isInProcessOrHigher$,
        })
            .pipe(
                take(1),
                switchMap(({ patient, visitNames, availableExpenses, elevatedAccess, isInProcessOrHigher }) =>
                    this.pdfGenerationService.generateExpenseReport({
                        request,
                        patient,
                        visitName: visitNames.find((x) => x.id == request.visitNameId),
                        availableExpenses,
                        attachmentUrls,
                        showConvertedAmounts: elevatedAccess === true && isInProcessOrHigher === true,
                    })
                )
            )
            .subscribe((blob) => window.open(URL.createObjectURL(blob), '_blank'));
    }

    isInProcessOrHigher$ = this.requestService.isInProcessOrHigher$;

    isVoided$ = this.request$.pipe(
        map((request) => {
            if (!request) return null;
            return request.status?.statusId === PaymentRequestStatus.Voided;
        })
    );
}
